import { loadQuery } from 'react-relay';
import moment from 'moment-timezone';
import RelayEnvironment from 'lib/utils/relay';
import { getAccountId, getUserId } from 'lib/utils/authentication';
import { USER_PROFILE_QUERY } from 'services/user/user.relay.services';
import { userProfileQuery } from 'services/user/__generated__/userProfileQuery.graphql';
import { EVENTS_QUERY } from 'services/event/event.relay.services';
import { eventsQuery } from 'services/event/__generated__/eventsQuery.graphql';
import { requestsQuery } from 'services/request/__generated__/requestsQuery.graphql';
import { REQUESTS_QUERY } from 'services/request/request.relay.services';
import { productsQuery } from 'services/product/__generated__/productsQuery.graphql';
import { PRODUCTS_QUERY } from 'services/product/product.relay.services';
import { teamsQuery } from 'services/team/__generated__/teamsQuery.graphql';
import { TEAMS_QUERY } from 'services/team/team.relay.services';
import { contactsQuery } from 'services/contact/__generated__/contactsQuery.graphql';
import { CONTACTS_QUERY } from 'services/contact/contact.relay.services';

const getStartOfTheWeek = (date: Date) => {
  return moment(date).startOf('day').isoWeekday(1).toDate();
};

const getEndOfTheWeek = (date: Date) => {
  return moment(date).endOf('day').isoWeekday(7).toDate();
};

export const HomePreloadData = () => {
  const userId = getUserId();

  return {
    userProfileRef: loadQuery<userProfileQuery>(RelayEnvironment, USER_PROFILE_QUERY, {
      id: userId,
      skip: !userId,
    }),
    eventsQueryRef: loadQuery<eventsQuery>(
      RelayEnvironment,
      EVENTS_QUERY,
      {
        first: 20,
        ordering: [{ sort: 'BEGINS_AT', direction: 'DESC' }],
        filter: {
          where: {
            userId: { equalTo: getUserId() },
            deletedAt: { isNull: true },
            status: { in: ['draft', 'published', 'completed', 'incomplete', 'cancelled'] },
            beginsAt: { moreThanOrEqualTo: getStartOfTheWeek(new Date()) },
            endsAt: { lessThanOrEqualTo: getEndOfTheWeek(new Date()) },
            or: {
              cancellationDatetime: { isNull: true },
              practitionerConfirmationDatetime: { notNull: true },
            },
          },
        },
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    unconfirmedEventsQueryRef: loadQuery<eventsQuery>(
      RelayEnvironment,
      EVENTS_QUERY,
      {
        first: 20,
        ordering: [{ sort: 'BEGINS_AT', direction: 'ASC' }],
        filter: {
          awaitingConfirmationOnly: true,
        },
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    requestsQueryRef: loadQuery<requestsQuery>(RelayEnvironment, REQUESTS_QUERY, {
      first: 1000,
      ordering: [{ sort: 'CREATED_AT', direction: 'DESC' }],
      filter: {
        where: {
          or: {
            accountId: { equalTo: getAccountId() },
            preferredUserId: { equalTo: userId },
          },
          event: {
            id: { isNull: true },
          },
          deletedAt: { isNull: true },
        },
      },
    }),
    teamsQueryRef: loadQuery<teamsQuery>(
      RelayEnvironment,
      TEAMS_QUERY,
      {
        userId: getUserId(),
        skip: false,
      },
      { fetchPolicy: 'store-or-network' },
    ),
    productsQueryRef: loadQuery<productsQuery>(RelayEnvironment, PRODUCTS_QUERY, {
      first: 100,
      filter: {
        where: {
          and: { accountId: { equalTo: getAccountId() } },
          deletedAt: { isNull: true },
        },
      },
    }),
    contactsQueryRef: loadQuery<contactsQuery>(
      RelayEnvironment,
      CONTACTS_QUERY,
      {
        first: 5000,
        ordering: [{ sort: 'CREATED_AT', direction: 'DESC' }],
        filter: {
          where: {
            and: {
              or: {
                accountId: { equalTo: getAccountId() },
                userId: { equalTo: userId },
                contactTeamMembers: { teamMember: { memberUserId: { equalTo: userId } } },
              },
              deletedAt: { isNull: true },
            },
          },
        },
      },
      { fetchPolicy: 'network-only' },
    ),
  };
};

export type HomePreloadDataType = ReturnType<typeof HomePreloadData>;
